// import BwModalComponent from './UI/BwModal.vue';

const helpers = {
	convertKeysToBoolean(data, keysToConvert) {
		// Ensure keysToConvert is an array (in case a single key is passed)
		const keysArray = Array.isArray(keysToConvert) ? keysToConvert : [keysToConvert];

		// Helper function to convert a single object
		const convertObject = obj => {
			return Object.keys(obj).reduce((acc, key) => {
				const value = obj[key];

				// Check if the current key is one of the keys to convert
				if(keysArray.includes(key)) {
					if(value === '0') {
						acc[key] = false;
					}
					else if(value === '1') {
						acc[key] = true;
					}
					else {
						acc[key] = value; // Keep the value as is if it's not '0' or '1'
					}
				}
				else if(typeof value === 'object' && value !== null) {
					// Recursively handle nested objects/arrays
					acc[key] = convertKeysToBoolean(value, keysArray);
				}
				else {
					acc[key] = value;
				}

				return acc;
			}, {});
		};

		// If data is an array, map over it and convert each object
		if(Array.isArray(data)) {
			return data.map(item => convertObject(item));
		}

		// If data is a single object, convert it
		if(typeof data === 'object' && data !== null) {
			return convertObject(data);
		}

		// Return the data unchanged if it's not an object or array
		return data;
	},

	getCharacterCount(htmlContent) {
		const textContent = this.getTextFromHTML(htmlContent);
		return textContent.length;
	},

	getTextFromHTML(htmlContent) {
		const tempElement = document.createElement('div');
		tempElement.innerHTML = htmlContent;
		return tempElement.textContent || tempElement.innerText || '';
	},

	encodeHTMLEntities(text) {
    return text.replace(/&/g, '&amp;')
               .replace(/</g, '&lt;')
               .replace(/>/g, '&gt;')
               .replace(/"/g, '&quot;')
               .replace(/'/g, '&#039;');
	},

	getLocalDateFormatted(d, f = 'MM-DD-YYYY hh:mm:ss a') {
		const utcMoment = moment.utc(d);
		const localMoment = utcMoment.local();
		return localMoment.format(f);
	},
	getDisplayLocalDate(field, tz = null, format = 'YYYY-MM-DD') {
		if(!field || field.startsWith('0000')) return null;
		return moment(this.getLocalDate(field, tz)).format(format);
	},
	getDisplayLocalDateTime(field, tz = null, format = 'YYYY-MM-DD hh:mm:ss a') {
		if(!field || field.startsWith('0000')) return null;
		return moment(this.getLocalDateTime(field, tz)).format(format);
	},
	getLocalDate(field, tz = null, format = 'YYYY-MM-DD') {
		if(!field || field.startsWith('0000')) return null;
		return Bandwango.dateToLocal(field, false, format, tz);
	},
	getLocalTime(field, tz = null, format = 'HH:mm:ss') {
		if(!field || field.startsWith('0000')) return null;
		return Bandwango.dateToLocal(field, false, format, tz);
	},
	getLocalDateTime(field, tz = null, format = 'YYYY-MM-DD HH:mm:ss') {
		if(!field || field.startsWith('0000')) return null;
		return Bandwango.dateToLocal(field, false, format, tz);
	},
	getUtcDate(value, tz = null) {
		return Bandwango.dateToUtc(value, 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', tz);
	},
	getUtcDateTime(date, time, tz = null) {
		return Bandwango.dateToUtc(date.substring(0, date.indexOf(' ')) + ' ' + time, 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm:ss', tz);
	},
	isObject(value) {
		return value !== null && typeof value === 'object' && !Array.isArray(value);
	},
	pick(originalObject = {}, keysToInclude = []) {
		return keysToInclude.reduce((object, key) => {
			if(originalObject.hasOwnProperty(key)) {
				object[key] = originalObject[key];
			}
			return object;
		}, {});
	},
	ucFirst(str) {
		return str && `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
	},
	ucWords(input) {
		return input.toLowerCase().replace(/(?:^|-|\s)\S/g, match => match.toUpperCase());
	},

	camelCaseToWords(str) {
		return str.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
	},

	getTemplateName(str) {
		return str?.replace(/^BW\s+|\s+Template$/g, '');
	},

	extractNames(entity) {
		// Initialize first_name and last_name
		let first_name = '';
		let last_name = '';

		// Check if the name field exists
		if(entity.hasOwnProperty('name')) {
			// Split the name into parts by space
			const nameParts = entity.name.trim().split(' ');

			// The last part is assumed to be the last name
			first_name = nameParts.shift();

			// The remaining parts are assumed to be the first name
			last_name = nameParts.join(' ');
		}
		else {
			// If name field doesn't exist, use first_name and last_name fields if available
			first_name = entity.first_name || '';
			last_name = entity.last_name || '';
		}

		return { first_name, last_name };
	},

	/**
	 * Auto-determines the most contrasting color based on the hex color provided
	 * This function also lives at: resources/js/bandwango.js
	 * @param hex - Hex color value
	 * @returns {string|string} - Black or white (#000000 or #FFFFFF)
	 */
	getContrastTextColor(hex) {
		if(!hex) return '#000000';

		// Remove '#' from the input if present
		const cleanHexColor = hex.replace('#', '');

		// Validate that the input is a valid 3 or 6 character hex color code
		if(!/^([0-9A-Fa-f]{3}){1,2}$/.test(cleanHexColor)) {
			throw new Error('Invalid hex color code');
		}

		// Convert 3 character hex color code to 6 character hex color code
		const sixCharHexColor =
				cleanHexColor.length === 3
						? cleanHexColor
								.split('')
								.map((char) => char + char)
								.join('')
						: cleanHexColor;

		// Calculate the relative luminance of the color
		const r = parseInt(sixCharHexColor.substring(0, 2), 16) / 255;
		const g = parseInt(sixCharHexColor.substring(2, 4), 16) / 255;
		const b = parseInt(sixCharHexColor.substring(4, 6), 16) / 255;

		const gammaCorrect = (value) =>
				value <= 0.03928
						? value / 12.92
						: Math.pow((value + 0.055) / 1.055, 2.4);

		const rCorrect = gammaCorrect(r);
		const gCorrect = gammaCorrect(g);
		const bCorrect = gammaCorrect(b);

		const luminance = 0.2126 * rCorrect + 0.7152 * gCorrect + 0.0722 * bCorrect;

		// Calculate contrast ratio and determine the most contrasting text color
		const blackContrastRatio = (luminance + 0.05) / 0.05;
		const whiteContrastRatio = 1.2 / (luminance + 0.05); // 1.05 was original

		return whiteContrastRatio >= blackContrastRatio ? '#FFFFFF' : '#000000';
	},

	getBaseUrl(partner, index, controller) {
		const portal = window.location.pathname.split('/')[1];
		let indexController = '';
		if(index && controller) {
			if(portal.includes('partner-portal')) {
				indexController = `/${index}`;
			}
			else {
				indexController = `/${index}/${controller}`;
			}
		}
		return (partner?.id)
				? `/${portal}/${partner.id}${indexController}`
				: `/${portal}${indexController}`;
	},

	callPhone(partnerUserId, phone) {
		window.open(`/superAdmin/dialerWindow?partnerUser=${partnerUserId}&phoneNumber=${phone}&autoDial=true`, 'targetWindow',
				`toolbar=no,
				location=no,
				status=no,
				menubar=no,
				scrollbars=yes,
				resizable=yes,
				width=300px,
				height=600px
				left=${window.screen.width - 300}
				top=${window.screen.height - 600}`);
	},

	async waitForRef(ref, timeout = 5000) {
		return new Promise((resolve, reject) => {
			const startTime = Date.now();

			function checkElement() {
				if(ref && ref.$el) {
					// Element is available, resolve the promise
					resolve(ref);
				}
				else if(Date.now() - startTime > timeout) {
					// Timeout reached, reject the promise
					reject(new Error('Timeout waiting for element'));
				}
				else {
					// Element not available yet, wait and check again
					setTimeout(checkElement, 100); // Adjust the interval as needed
				}
			}

			checkElement(); // Start checking for the element
		});
	},

	redemptionValidationCase(redemtionValidation) {
		return !redemtionValidation ? '' : redemtionValidation === 'qr'
				? 'QR Code'
				: this.camelCaseToWords(redemtionValidation);
	}

};

export function getDefaultProps(component) {
	return Object.keys(component.options.props).reduce((acc, propName) => {
		const propInfo = component.options.props[propName];
		const hasDefault = propInfo.default !== undefined;
		const isRequired = propInfo.required === true;
		const isBoolean = propInfo.type === Boolean;

		// Include the prop in defaultProps if it is required or has a default value
		if(isRequired || hasDefault) {
			acc[propName] = isBoolean ? propInfo.default || false : (hasDefault
					? propInfo.default
					: null);
		}

		return acc;
	}, {});
}

export function PlaceholderMedia() {
	// TODO: support other locations in the future
	this.cropperSettings = {
		limitAR: '2:1',
		x: 0,
		y: 0
	};
	this.location = 'Header';
	this.name = 'Header';
	this.url = '/images/placehold.co.png';
}

// Ignore for now
export function BwModalProps(modalObj = {}) {
	const BwModalComponent = window.Vue.component('BwModal');
	const defaultProps = getDefaultProps(BwModalComponent);
	return { ...defaultProps, ...modalObj };
}

export function Place(place) {
	const noImage = {
		url: '/images/placehold.co.png'
	};
	const noMedia = new PlaceholderMedia();
	const hasDealVenue = place
			&& place.hasOwnProperty('deal_venue')
			&& place.deal_venue !== null;

	// If we have place media we want the Header first
	// if(place) {
	// 	if(Array.isArray(place.media)) {
	// 		place.media = place.deal_venue?.media?.filter(media => media.location === 'Header')
	// 				|| place.media?.filter(media => media.location === 'Header')
	// 				|| [];
	// 	}
	// 	// TODO: Remove this when the above is fixed
	// 	else if(place.media?.url) {
	// 		place.media = [
	// 			{
	// 				...place.media,
	// 				location: 'Header'
	// 			}
	// 		];
	// 	}
	// }

	this.checkIns = place?.checkIns || [];
	this.checkIn = place?.checkIn;
	this.checkInQrCodeImageUrl = place?.checkInQrCodeImageUrl || null;
	this.description = place?.description || null;
	this.formatted_address = place?.formatted_address || place?.address || null;
	this.formatted_phone_number = place?.formatted_phone_number || place?.phone || null;
	this.geometry = place?.geometry || {
		location: {
			lat: isNaN(parseFloat(place?.latitude)) ? null : parseFloat(place?.latitude),
			lng: isNaN(parseFloat(place?.longitude)) ? null : parseFloat(place?.longitude)
		}
	};
	this.id = place?.id || null;
	this.media = place?.media?.length && place.media || [noMedia];
	this.name = place?.name || null;
	this.pin_number = place?.pin_number || null;
	this.place_id = place?.place_id || place?.google_place_id || null;
	this.types = place?.types || place?.google_place_types || [
		'point_of_interest'
	];
	this.venueName = place?.name || null;
	this.website = place?.website || null;
	this.overwrittenNames = place?.overwrittenNames || [];

	if(hasDealVenue) {
		this.image = place?.image || noImage;
		this.deal_venue = {
			...place.deal_venue,
			media: [
				...(place.deal_venue?.media || [noMedia])
			]
		};
	}

	// Delete some keys that can cause app confusion
	if(place?.address) delete place.address;
	if(place?.latitude) delete place.latitude;
	if(place?.longitude) delete place.longitude;
	if(place?.google_place_id) delete place.google_place_id;
	if(place?.google_place_types) delete place.google_place_types;
	if(place?.phone) delete place.phone;

	// Add any other keys from the provided place
	if(place) {
		for (let key in place) {
			if(!this.hasOwnProperty(key)) {
				this[key] = place[key];
			}
		}
	}
}

export function BwPlace(place) {
	const hasDealVenue = place && place.hasOwnProperty('deal_venue');

	// const media = place?.media?.map(m => {
	// 	if(m.id.startsWith('bw-'))
	// 		m.id = null;
	//
	// 	return m;
	// });
	const { removeMedia, media = [] } = place;

	if(removeMedia?.length) {
		removeMedia.forEach(toRemove => {
			if(!toRemove.id) return;

			toRemove['flag'] = 'delete';
			media.push(toRemove);
		});
	}

	this.address = place?.formatted_address || place?.address || null;
	this.audio = place?.audio?.url ? place.audio : null;
	this.checkIn = place?.checkIn || null;
	this.checkIns = place?.checkIns || null;
	this.description = place?.description || null;
	this.google_place_id = place?.place_id || null;
	this.google_place_types = place?.types || null;
	this.id = place?.id || null;
	this.latitude = place?.geometry?.location?.lat || null;
	this.longitude = place?.geometry?.location?.lng || null;
	this.media = media || null;
	this.name = place?.name || null;
	this.phone = place?.formatted_phone_number || place?.phone || null;
	this.pin_number = place?.pin_number || null;
	this.skipAIDescription = place?.skipAIDescription || null;
	this.website = place?.website || null;
	this.hours = place?.hours || [];
	this.video = place?.video?.url ? place.video : null;

	if(hasDealVenue) this.deal_venue = place.deal_venue;
}

export function resizeCloudinaryImage(url, width, height) {
	if(!url) return null;

	const splitURL = url.split('/');
	const transformationIndex = splitURL.findIndex(part => part === 'upload') + 1;

	if(transformationIndex === -1) return url;

	const existingTransformations = splitURL[transformationIndex].split(',');

	// Remove existing w_ and h_ transformations
	const filteredTransformations = existingTransformations.filter(t => !t.startsWith('w_') && !t.startsWith('h_'));

	// Add new w_ and h_ transformations
	const newTransformations = [
		`w_${width}`,
		`h_${height}`,
		'c_scale',
		...filteredTransformations
	];

	splitURL[transformationIndex] = newTransformations.join(',');

	return splitURL.join('/');
}

export function intersectDeepKeysAndValues(arr) {
	return {};
	// let entries = Object.entries(arr[0]);
	//
	// entries = entries.reduce((acc, [key, value]) => {
	// 	if(typeof value === 'object' && value !== null && !Array.isArray(value)) {
	// 		const nestedIntersection = intersectDeepKeysAndValues(arr.map(obj => obj[key] || {}));
	// 		if(Object.keys(nestedIntersection).length > 0) {
	// 			acc.push([key, nestedIntersection]);
	// 		}
	// 	}
	// 	else if(arr.every(obj => obj && obj.hasOwnProperty(key) && obj[key] === value)) {
	// 		acc.push([key, value]);
	// 	}
	// 	return acc;
	// }, []);
	//
	// const intersectionObj = Object.fromEntries(entries);
	//
	// return intersectionObj;
}

export function getNonIntersecting(objects) {
	return {};
	// let result = objects.map((obj, index) => {
	// 	let intersection = intersectDeepKeysAndValues(objects.filter((_, i) => i !== index));
	// 	return getDifference(obj, intersection);
	// });
	//
	// return result;
}

// function getDifference(obj1, obj2) {
// 	let result = {};
//
// 	for (let key in obj1) {
// 		if(typeof obj1[key] === 'object' && obj1[key] !== null && obj2[key]) {
// 			let diff = getDifference(obj1[key], obj2[key]);
// 			if(Object.keys(diff).length > 0) {
// 				result[key] = diff;
// 			}
// 		}
// 		else if(!_.isEqual(obj1[key], obj2[key])) {
// 			result[key] = obj1[key];
// 		}
// 	}
//
// 	return result;
// }

export async function copyToClipboard(data, message) {
	try {
		await navigator.clipboard.writeText(data);
		window.EventBus.$emit('system-messages', [
			{
				message: message || 'Copied to clipboard',
				messageType: 'success',
				showOverlay: true,
				timeout: 2000
			}
		]);
	}
	catch (error) {
		window.EventBus.$emit('system-messages', [
			{ message: error.message, messageType: 'error' }
		]);
	}
}

export function calculateAR(limitAR) {
	return limitAR.split(':').reduce((a, b) => a / b);
}

export function wrapAutocompleteSearchTextResult(str, search) {
	if(!search) {
		return str;
	}
	const index = str.toLowerCase().indexOf(search.toLowerCase());
	if(index === -1) {
		return str; // Search string not found
	}
	const beforeMatch = str.slice(0, index);
	const match = str.slice(index, index + search.length);
	const afterMatch = str.slice(index + search.length);
	return `${beforeMatch}<span class="v-list-item__mask">${match}</span>${afterMatch}`;
}

export { helpers };
